import BigNumber from 'bignumber.js';
import { SxProp, View } from 'dripsy';
import React, { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LayoutContext } from 'contexts/LayoutContext';
import { CurrenciesContext } from '@/currencies/currency.context';
import { UniButton } from '@/design-system/components/buttons';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { UniInputAmount } from '@/design-system/components/inputs';
import { ModalHeader } from '@/design-system/components/modal';
import { UniTabHeader } from '@/design-system/components/tabs';
import { TextBody } from '@/design-system/components/typography';
import { formatAmountByDecimals } from '@/utils/numberFormatting';
import { UserEarnActionListResponseDataItem } from '@utx/utx-client';
import { CurrenciesIcon } from '../../components';
import { AmountSummary } from '../../components/cards/AmountSummary';
import { getEarnTenorTranslationKey } from '../../utils/getEarnTenorTranslationKey';
import { RedeemStackRoutes, RedeemStackScreenProps } from './types';

export const RedeemEdit: React.FC<RedeemStackScreenProps<RedeemStackRoutes.Edit>> = ({ navigation, route }) => {
  const { subscription, onLayout } = route.params;

  const layout = useContext(LayoutContext);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const [formValues, setFormValues] = useState<{
    redemptionAmount?: number;
    isMax?: boolean;
  }>({
    redemptionAmount: undefined,
    isMax: false
  });

  const errorText = useMemo(() => {
    if (!formValues.redemptionAmount) return undefined;
    const redemptionAmountBN = new BigNumber(formValues.redemptionAmount || 0);
    const rewardsAccrued = new BigNumber(subscription.rewardsAccrued || 0);
    const subscriptionAmount = new BigNumber(subscription.amount || 0);
    if (redemptionAmountBN.gt(rewardsAccrued.plus(subscriptionAmount))) {
      return 'Redemption amount cannot be greater than rewards accrued plus subscription amount';
    }
    return undefined;
  }, [formValues.redemptionAmount]);

  return (
    <View
      sx={{
        height: layout.isMobile ? '100%' : 'auto',
        width: layout.dimensions.width,
        maxWidth: layout.isMobile ? layout.dimensions.width : Math.min(500, layout.dimensions.width),
        backgroundColor: '$backgroundPrimary'
      }}
      onLayout={(event) => {
        const { width, height } = event.nativeEvent.layout;
        setDimensions({ width, height });
        onLayout(event);
      }}>
      {/* Content */}
      <Header
        title={subscription.offerName}
        isMobile={layout.isMobile}
        onClose={() => navigation.navigate(RedeemStackRoutes.Main, { ...route.params, goBack: true })}
      />
      <Body
        redemptionAmount={formValues.redemptionAmount}
        subscription={subscription}
        onChangeRedemptionAmount={(value, isMax) => {
          setFormValues({ redemptionAmount: value, isMax });
        }}
        onBack={() => navigation.navigate(RedeemStackRoutes.Main, { ...route.params, goBack: true })}
        onRedeem={() => {
          if (!formValues.redemptionAmount) return;
          navigation.navigate(RedeemStackRoutes.Confirm, {
            ...route.params,
            netRedemptionAmount: new BigNumber(formValues.redemptionAmount).toString(),
            isMax: formValues.isMax
          });
        }}
        disabled={!formValues.redemptionAmount || !!errorText}
        errorText={errorText}
      />
    </View>
  );
};

const Header = ({ title, isMobile, onClose }: { title: string; isMobile: boolean; onClose: () => void }) => {
  if (isMobile) {
    return <UniTabHeader title={title} onBack={onClose} />;
  }

  return <ModalHeader title={title} closeModal={onClose} />;
};

const Body = ({
  subscription,
  disabled,
  onChangeRedemptionAmount,
  onBack,
  onRedeem,
  errorText,
  redemptionAmount
}: {
  subscription: UserEarnActionListResponseDataItem;
  onChangeRedemptionAmount: (amount: number | undefined, isMax?: boolean) => void;
  onBack: () => void;
  onRedeem: () => void;
  disabled: boolean;
  errorText?: string;
  redemptionAmount?: number;
}) => {
  const { t } = useTranslation('earn');
  const { getDecimalByAsset } = useContext(CurrenciesContext);

  const layout = useContext(LayoutContext);

  const styles = layout.isMobile ? stylesNative : stylesWeb;

  const tenorTranslation = getEarnTenorTranslationKey(subscription.tenor);

  return (
    <View
      sx={{
        ...styles.container,
        padding: layout.isMobile ? '$4' : '$7',
        gap: '$5',
        justifyContent: layout.isMobile ? 'space-between' : 'flex-start'
      }}>
      <View sx={{ gap: '$7' }}>
        <CurrenciesIcon
          asset={subscription.asset}
          title={subscription.offerName}
          subText={subscription.provider}
          sx={styles.currenciesIcon}
        />
        <View sx={styles.amountSummary}>
          <AmountSummary label={t('earn-invest::glossary.principle')} value={subscription.amount} asset={subscription.asset} />
          <AmountSummary label={t('earn-invest::glossary.est-apy')} value={subscription.estimatedApyPercent} asset="%" alignRight />
        </View>
        <View sx={styles.amountSummary}>
          <AmountSummary
            label={t('earn-invest::glossary.rewards-accrued')}
            value={subscription.rewardsAccrued}
            asset={subscription.asset}
            success
          />
          <AmountSummary label={t('earn-invest::glossary.tenor')} value={t(tenorTranslation.key, tenorTranslation.options)} alignRight />
        </View>
        <UniInputAmount
          labelText={t('earn-invest::glossary.redemption-amount')}
          subText={formatAmountByDecimals({
            value: subscription.amount,
            decimals: getDecimalByAsset(subscription.asset),
            suffix: subscription.asset
          })}
          asset={subscription.asset}
          max={new BigNumber(subscription.amount).plus(subscription.rewardsAccrued).toNumber()}
          onValueChange={onChangeRedemptionAmount}
          errorText={errorText}
          errorTextPadding={36}
        />
        <View sx={styles.amountDetails}>
          <TextBody secondary>{t('earn-invest::glossary.net-amount')}</TextBody>
          <TextBody>
            {redemptionAmount
              ? formatAmountByDecimals({
                  value: redemptionAmount,
                  decimals: getDecimalByAsset(subscription.asset),
                  suffix: subscription.asset
                })
              : ' - '}
          </TextBody>
        </View>
      </View>
      <View sx={{ mt: '$5', flexDirection: 'row', gap: '$2' }}>
        {!layout.isMobile && (
          <UniButton
            size="default"
            variant={ButtonVariant.outline1}
            onPress={onBack}
            label={t('earn-invest::glossary.back')}
            wrapperSx={{ flex: 1 }}
          />
        )}
        <UniButton
          size="default"
          onPress={onRedeem}
          label={t('earn-invest::glossary.place-instruction')}
          wrapperSx={{ flex: 1 }}
          disabled={disabled}
        />
      </View>
    </View>
  );
};

enum StyleName {
  container = 'container',
  currenciesIcon = 'currenciesIcon',
  amountSummary = 'amountSummary',
  progressCard = 'progressCard',
  autoRenewalSwitch = 'autoRenewalSwitch',
  modalHeader = 'modalHeader',
  amountDetails = 'amountDetails'
}

const stylesWeb: Record<StyleName, SxProp> = {
  container: { width: 500, padding: '$7', pb: '$10' },
  currenciesIcon: {},
  amountSummary: {
    mb: '$5',
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  progressCard: { mb: '$5' },
  autoRenewalSwitch: { mb: 0 },
  modalHeader: {
    flex: 1,
    padding: '$7',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  amountDetails: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  }
};

const stylesNative: Record<StyleName, SxProp> = {
  container: { flex: 1, paddingX: '$4', pb: '$5', width: '100%', justifyContent: 'space-between' },
  currenciesIcon: {},
  amountSummary: {
    mb: '$5',
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  progressCard: { mb: '$5' },
  autoRenewalSwitch: { mb: 0 },
  modalHeader: {
    flex: 1,
    padding: '$7',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  amountDetails: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  }
};
